import {
  CovideoGTMVehicleItem,
  CovideoGTMVideoAttributes,
  GTMVehicleAttributes,
  GTMVideoAttributes,
  PageLocation,
  PageSection,
} from './types';

/**
 * Convert a duration to decimal minutes.
 * Pass API value (ms or s) and get minutes rounded to 2 decimals.
 *
 * @example toDecimalMinutes(50600, 'ms') // 0.84  (≈ 50.6s)
 * @example toDecimalMinutes(50, 's')     // 0.83  (50s)
 * @example toDecimalMinutes(165, 's')    // 2.75  (2m45s)
 */
export function toDecimalMinutes(
  value: number,
  unit: 'ms' | 's' = 'ms',
  fractionDigits = 2
): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  const seconds = unit === 'ms' ? value / 1000 : value;
  const minutes = seconds / 60;
  const factor = 10 ** fractionDigits;
  return Math.round(minutes * factor) / factor;
}

export function mapToGTMVideoAttributes(
  src?: CovideoGTMVideoAttributes
): GTMVideoAttributes {
  if (!src) {
    return {} as GTMVideoAttributes;
  }

  return {
    video_id: Number(src.videoId),
    video_type: src.videoType ?? undefined,
    video_customer_name: Boolean(src.videoCustomerName),
    video_length: toDecimalMinutes(Number(src.videoLength)) || 0,
    video_title: src.videoTitle || undefined,
  };
}

/**
 * Map app-level meeting attributes to GTM meeting attributes.
 */

export function mapToGtmVehicleAttributes(
  item: CovideoGTMVehicleItem
): GTMVehicleAttributes {
  const vehicleData: GTMVehicleAttributes = !!item
    ? {
        vehicle: item?.title || undefined,
        price: item?.price ? parseFloat(item?.price.toString()) : undefined,
        vin: item?.vin || undefined,
        stock_number: item?.stockNumber?.toString() || undefined,
        condition: item?.itemCondition || undefined,
        engine: item?.engine || undefined,
        make: item?.make || undefined,
        model: item?.model || undefined,
        trim: item?.trim || undefined,
        year: item?.year ? parseInt(item?.year.toString(), 10) : undefined,
        mileage: item?.mileage
          ? parseInt(item?.mileage.toString(), 10)
          : undefined,
        ext_color: item?.exteriorColor || undefined,
        int_color: item?.interiorColor || undefined,
        transmission: item?.transmission || undefined,
      }
    : {};
  return vehicleData;
}

// deliveryTime format: "YYYY-MM-DD;HH:MM;AM/PM" (example)
// Returns { meetingDate?: string, meetingTime?: string }
export function mapDeliveryTimeToGtmMeeting(deliveryTime?: string) {
  const [date, t1, t2] = (deliveryTime ?? '').split(';');

  const meetingDate = date?.trim() ? date.trim() : undefined;
  const meetingTime =
    t1?.trim() && t2?.trim() ? `${t1.trim()} ${t2.trim()}` : undefined;

  return { meetingDate, meetingTime };
}

export const TRACKED_PAGES = {
  INVENTORY_DETAILS: {
    pageLocation: PageLocation.INVENTORY,
    pageSection: PageSection.DETAILS,
  },
  EMBED_SEND_AND_SHARE: {
    pageLocation: PageLocation.SEND_AND_SHARE,
    pageSection: PageSection.EMBED,
  },
  SEND_SEND_AND_SHARE: {
    pageLocation: PageLocation.SEND_AND_SHARE,
    pageSection: PageSection.SEND,
  },
  SHARE_SEND_AND_SHARE: {
    pageLocation: PageLocation.SEND_AND_SHARE,
    pageSection: PageSection.SHARE,
  },
  MEETING_SCHEDULE: {
    pageLocation: PageLocation.MEETINGS,
    pageSection: PageSection.SCHEDULE,
  },
  MEETING_DETAILS: {
    pageLocation: PageLocation.MEETINGS,
    pageSection: PageSection.DETAILS,
  },
  VIDEOS_RECORD: {
    pageLocation: PageLocation.VIDEOS,
    pageSection: PageSection.RECORD,
  },
  VIDEOS_RECORDER: {
    pageLocation: PageLocation.VIDEOS,
    pageSection: PageSection.RECORDER,
  },
  VIDEOS_DETAILS: {
    pageLocation: PageLocation.VIDEOS,
    pageSection: PageSection.DETAILS,
  },
  VIDEOS_MERGE: {
    pageLocation: PageLocation.VIDEOS,
    pageSection: PageSection.MERGE,
  },
  VIDEOS_VOICE_RECORDER: {
    pageSection: PageLocation.VIDEOS,
    pageLocation: PageSection.VOICE_RECORDER,
  },
} as const;
